import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api } from '../api';
import type { Episode, MediaItem, NarrationStatus } from '../types';
import FilmPlayer from '../player/FilmPlayer';
import EditEpisodeDialog from '../components/EditEpisodeDialog';
import StoryPanel from '../components/StoryPanel';
import PeoplePanel from '../components/PeoplePanel';
import ShareDialog, { roleLabel } from '../components/ShareDialog';
import { formatDateRange, formatDuration, formatFullDate } from '../lib/format';

/**
 * Two modes in one component:
 *   /episode/:id -> detail page with cover, description and PLAY STORY button
 *   /play/:id    -> fullscreen story player
 * Sharing the component keeps the data fetching in one place.
 */
export default function EpisodePage({ fullscreen = false }: { fullscreen?: boolean }) {
  const { episodeId } = useParams();
  const navigate = useNavigate();
  const [search] = useSearchParams();
  // "Preview in English / Urdu" from the story panel.
  const langParam = search.get('lang');
  const langOverride = langParam === 'en' || langParam === 'ur' ? langParam : null;
  // Export mode: the server's hidden Chrome records the film (never saves watch progress).
  const exportParam = search.get('export');
  const exportLang = exportParam === 'en' || exportParam === 'ur' ? exportParam : null;
  // Player only: narration clips and their lengths, so every memory holds for its line.
  const [narration, setNarration] = useState<NarrationStatus | null>(null);

  const [episode, setEpisode] = useState<Episode | null>(null);
  const [items, setItems] = useState<MediaItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [sharing, setSharing] = useState(false);
  // Player only: the season's (possibly renamed) title and the next story.
  const [seasonContext, setSeasonContext] = useState<{
    title: string | null;
    next: { id: string; title: string } | null;
  } | null>(null);

  /** Reload the episode and its memories after a photo is taken out. */
  const reloadMedia = async () => {
    if (!episode) return;
    const [ep, media] = await Promise.all([api.episode(episode.id), api.episodeMedia(episode.id)]);
    setEpisode((cur) => (cur ? { ...cur, ...ep } : ep));
    setItems(media.items);
  };

  useEffect(() => {
    if (!episodeId) return;
    let cancelled = false;
    setError(null);
    // Clear the previous story so "Next story" never flashes stale content.
    setEpisode(null);
    setItems([]);
    setSeasonContext(null);
    Promise.all([api.episode(episodeId), api.episodeMedia(episodeId)])
      .then(async ([ep, media]) => {
        if (cancelled) return;
        if (fullscreen) {
          const [season, narr] = await Promise.all([
            api.season(ep.year, ep.access && !ep.access.isOwner ? ep.access.owner?.id : null).catch(() => null),
            ep.story ? api.narrationStatus(ep.id).catch(() => null) : Promise.resolve(null),
          ]);
          setNarration(narr);
          const list = season?.episodes ?? [];
          const at = list.findIndex((e) => e.id === ep.id);
          const next = at >= 0 ? list[at + 1] : undefined;
          if (cancelled) return;
          setSeasonContext({ title: season?.title ?? null, next: next ? { id: next.id, title: next.title } : null });
        }
        setEpisode(ep);
        setItems(media.items);
      })
      .catch((err) => {
        if (!cancelled) setError((err as Error).message);
      });
    return () => {
      cancelled = true;
    };
  }, [episodeId, fullscreen]);

  const saveProgress = useCallback((id: string, seconds: number, completed: boolean) => {
    void api.saveProgress(id, seconds, completed);
  }, []);

  const exitPlayer = useCallback(() => {
    navigate(`/episode/${episodeId}`);
  }, [navigate, episodeId]);

  const videos = useMemo(() => items.filter((i) => i.type === 'VIDEO').length, [items]);

  if (fullscreen) {
    if (error) {
      return <FullScreenMessage title="Can't play this story" body={error} onExit={exitPlayer} />;
    }
    if (!episode || items.length === 0 || !seasonContext) {
      // A quiet warm-black frame while the story loads, not a flash of text.
      return <div className="fixed inset-0 z-50 bg-[#0b0907]" aria-busy="true" aria-label="Loading story" />;
    }
    return (
      <FilmPlayer
        key={episode.id}
        episode={episode}
        items={items}
        seasonTitle={seasonContext.title}
        nextEpisode={seasonContext.next}
        onExit={exitPlayer}
        onNext={(id) => navigate(`/play/${id}`)}
        onSaveProgress={exportLang ? () => undefined : saveProgress}
        canDownload={episode.access?.isOwner ?? true}
        narration={narration}
        langOverride={langOverride}
        exportLang={exportLang}
      />
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-xl px-6 py-32 text-center">
        <h1 className="mb-2 text-2xl font-bold">Episode unavailable</h1>
        <p className="text-white/55">{error}</p>
      </div>
    );
  }

  if (!episode) {
    return (
      <div className="mx-auto max-w-[1400px] px-4 py-10 sm:px-6 lg:px-10">
        <div className="skeleton mb-6 aspect-video w-full" />
        <div className="skeleton mb-3 h-8 w-64" />
        <div className="skeleton h-4 w-96" />
      </div>
    );
  }

  // What the signed-in user may do here (their own episode, or a share level).
  const role = episode.access?.role ?? 'OWNER';
  const isOwner = role === 'OWNER';
  const canEdit = isOwner || role === 'EDITOR';
  const canStory = canEdit || role === 'CONTRIBUTOR';
  const sharedOwner = !isOwner ? (episode.access?.owner ?? null) : null;

  return (
    <div>
      <EpisodeHero
        episode={episode}
        itemCount={items.length}
        videoCount={videos}
        onEdit={canEdit ? () => setEditing(true) : undefined}
        onShare={isOwner ? () => setSharing(true) : undefined}
        sharedBy={sharedOwner ? `Shared by ${sharedOwner.name} · ${roleLabel(role as Exclude<typeof role, 'OWNER'>)}` : null}
        onPlay={() => navigate(`/play/${episode.id}`)}
        onBack={() => (sharedOwner ? navigate('/') : navigate(`/season/${episode.year}`))}
      />

      {sharing && (
        <ShareDialog episodeId={episode.id} episodeTitle={episode.title} year={episode.year} onClose={() => setSharing(false)} />
      )}

      {editing && (
        <EditEpisodeDialog
          episode={episode}
          items={items}
          onPhotosChanged={reloadMedia}
          onClose={() => setEditing(false)}
          onSaved={(updated) => {
            // Keep detail-only fields (label, dateLabel, event) from the first load.
            setEpisode({ ...episode, ...updated });
            setEditing(false);
          }}
        />
      )}

      {/* ---------- Description + gallery ---------- */}
      <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-10">
        <section className="mb-10">
          <h2 className="mb-3 text-sm font-semibold tracking-wide text-white/50 uppercase">Description</h2>
          {episode.description || !canEdit ? (
            <p className="max-w-2xl text-base leading-relaxed whitespace-pre-line text-white/70">
              {episode.description || 'No description yet.'}
            </p>
          ) : (
            <button
              onClick={() => setEditing(true)}
              className="focus-ring rounded text-base text-white/50 underline decoration-white/20 underline-offset-4 hover:text-white/80"
            >
              Write what you remember about this day
            </button>
          )}
          {episode.event && episode.event.signals.length > 0 && (
            <p className="mt-4 text-xs text-white/30">
              Grouped automatically by {episode.event.signals.join(' + ').toLowerCase().replace(/_/g, ' ')} (
              confidence {Math.round(episode.event.confidence * 100)}%)
              {episode.startDate && ` · ${formatFullDate(episode.startDate)}`}
            </p>
          )}
        </section>

        <section id="people" className="mb-10 max-w-3xl scroll-mt-24" aria-labelledby="people-heading">
          <h2 id="people-heading" className="mb-1 text-sm font-semibold tracking-wide text-white/50 uppercase">
            People
          </h2>
          <p className="mb-3 text-sm text-white/45">
            {canStory ? 'Name who’s here, so the story can use their names.' : 'Who’s in these memories.'}
          </p>
          <PeoplePanel episodeId={episode.id} ownerId={sharedOwner?.id ?? null} readOnly={!canStory} />
        </section>

        <StoryPanel
          episodeId={episode.id}
          items={items}
          story={episode.story}
          title={episode.title}
          backdropUrl={episode.backdropUrl ?? null}
          dateLine={formatDateRange(episode.startDate, episode.endDate) || null}
          mood={episode.setup?.mood ?? null}
          storyLanguage={episode.setup?.storyLanguage ?? null}
          voiceGender={episode.setup?.voiceGender ?? null}
          narrator={episode.setup?.narrator ?? null}
          coverUrl={episode.coverUrl ?? null}
          onStory={(story) => setEpisode((ep) => (ep ? { ...ep, story } : ep))}
          canEdit={canStory}
          canDownload={isOwner}
        />

        <section>
          <h2 className="mb-4 text-sm font-semibold tracking-wide text-white/50 uppercase">
            {items.length} {items.length === 1 ? 'Memory' : 'Memories'}
          </h2>
          <MemoryGrid
            items={items}
            onRemove={
              canEdit
                ? async (id) => {
                    await api.removeEpisodeMedia(episode.id, id);
                    await reloadMedia();
                  }
                : undefined
            }
          />
        </section>
      </div>
    </div>
  );
}
interface HeroProps {
  episode: Episode;
  itemCount: number;
  videoCount: number;
  /** Missing = the user may not edit (shared at a lower level). */
  onEdit?: () => void;
  /** Owner only. */
  onShare?: () => void;
  sharedBy?: string | null;
  onPlay: () => void;
  onBack: () => void;
}

/** Large cinematic backdrop + title + PLAY STORY button. */
function EpisodeHero(props: HeroProps) {
  const { episode, itemCount, videoCount } = props;
  const photoCount = Math.max(0, itemCount - videoCount);

  return (
    <div className="relative mb-8 h-[46vh] min-h-[300px] w-full overflow-hidden">
      {episode.backdropUrl && (
        <img src={episode.backdropUrl} alt="" className="absolute inset-0 h-full w-full object-cover" />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-ink-950 via-ink-950/70 to-ink-950/30" />
      <div className="absolute inset-0 bg-gradient-to-r from-ink-950/90 to-transparent" />

      <div className="absolute inset-x-0 bottom-0 mx-auto max-w-[1400px] px-4 pb-8 sm:px-6 lg:px-10">
        <button
          onClick={props.onBack}
          className="focus-ring mb-4 flex items-center gap-2 rounded text-sm text-white/55 transition hover:text-white"
        >
          <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M15 18l-6-6 6-6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          {props.sharedBy ? 'Home' : `Season ${episode.year}`}
        </button>
        {props.sharedBy && (
          <div className="mb-2 inline-block rounded-full bg-white/10 px-3 py-1 text-xs font-medium text-white/75 ring-1 ring-white/15">
            {props.sharedBy}
          </div>
        )}

        <h1 className="mb-3 max-w-3xl text-3xl leading-tight font-extrabold tracking-tight sm:text-5xl">
          {episode.title}
        </h1>

        {episode.locationName && (
            <div className="mb-2 text-xs font-semibold tracking-[0.15em] text-accent-soft uppercase">
              {episode.locationName}
            </div>
          )}
          {formatDateRange(episode.startDate, episode.endDate) && (
            <div className="mb-3 text-sm font-medium text-white/55">
              {formatDateRange(episode.startDate, episode.endDate)}
            </div>
          )}
          <div className="mb-5 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-white/60">
            <span>
              {photoCount} {photoCount === 1 ? 'photo' : 'photos'}
            </span>
            {videoCount > 0 && (
              <>
                <span className="text-white/25">•</span>
                <span>
                  {videoCount} {videoCount === 1 ? 'video' : 'videos'}
                </span>
              </>
            )}
            <span className="text-white/25">•</span>
            <span>
              {itemCount} {itemCount === 1 ? 'memory' : 'memories'}
            </span>
            {episode.durationSeconds > 0 && (
              <>
                <span className="text-white/25">•</span>
                <span title="Photos × photo duration, plus video lengths">
                  ~{formatDuration(episode.durationSeconds)}
                </span>
              </>
            )}
          </div>

        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={props.onPlay}
            disabled={itemCount === 0}
            className="focus-ring flex items-center gap-2.5 rounded-md bg-white px-7 py-3 text-sm font-bold text-black transition hover:bg-white/85 disabled:opacity-40"
          >
            <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor">
              <path d="M8 5v14l11-7z" />
            </svg>
            PLAY STORY
          </button>
          {props.onEdit && (
            <button
              onClick={props.onEdit}
              className="focus-ring flex items-center gap-2 rounded-md bg-white/15 px-5 py-3 text-sm font-semibold backdrop-blur transition hover:bg-white/25"
            >
              <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path d="M4 20h4L19 9l-4-4L4 16v4z" strokeLinejoin="round" />
              </svg>
              Edit details
            </button>
          )}
          {props.onShare && (
            <button
              onClick={props.onShare}
              className="focus-ring flex items-center gap-2 rounded-md bg-white/15 px-5 py-3 text-sm font-semibold backdrop-blur transition hover:bg-white/25"
            >
              <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <circle cx="18" cy="5" r="3" />
                <circle cx="6" cy="12" r="3" />
                <circle cx="18" cy="19" r="3" />
                <path d="M8.6 13.5l6.8 4M15.4 6.5l-6.8 4" />
              </svg>
              Share
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
/** Square thumbnail grid of every memory in the episode. × takes one out (two taps: it asks first). */
function MemoryGrid({ items, onRemove }: { items: MediaItem[]; onRemove?: (id: string) => Promise<void> }) {
  const [pending, setPending] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const take = async (id: string) => {
    if (!onRemove) return;
    setBusy(true);
    setError(null);
    try {
      await onRemove(id);
      setPending(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
        {items.map((item, i) => (
          <figure key={item.id} className="group relative overflow-hidden rounded-lg bg-ink-850">
            <div className="aspect-square">
              {item.type === 'VIDEO' ? (
                <video
                  src={item.url}
                  poster={item.posterUrl ?? undefined}
                  preload="none"
                  muted
                  playsInline
                  className="h-full w-full object-cover"
                />
              ) : (
                <img
                  src={item.thumbnailUrl ?? item.url}
                  alt={item.originalFilename}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              )}
            </div>
            <span className="absolute top-1 left-1 rounded bg-black/70 px-1.5 text-[10px] font-semibold">{i + 1}</span>
            {item.type === 'VIDEO' && (
              <span className="absolute right-1 bottom-1 rounded bg-black/70 p-1">
                <svg viewBox="0 0 24 24" className="h-3 w-3 text-white" fill="currentColor">
                  <path d="M8 5v14l11-7z" />
                </svg>
              </span>
            )}
            {onRemove &&
              (pending === item.id ? (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/80 p-2 text-center">
                  <p className="text-[11px] text-white/80">Take it out? It goes back to your library.</p>
                  <button
                    type="button"
                    disabled={busy || items.length <= 1}
                    onClick={() => void take(item.id)}
                    className="focus-ring w-full rounded bg-red-500/90 px-2 py-1.5 text-xs font-semibold text-white disabled:opacity-40"
                  >
                    Take out
                  </button>
                  <button type="button" onClick={() => setPending(null)} className="focus-ring text-xs text-white/70 underline">
                    Keep
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  disabled={busy || items.length <= 1}
                  onClick={() => setPending(item.id)}
                  aria-label={`Take memory ${i + 1} out of this episode`}
                  title={items.length <= 1 ? 'An episode needs at least one memory' : 'Take out of this episode'}
                  className="focus-ring absolute top-1 right-1 grid h-6 w-6 place-items-center rounded-full bg-black/75 text-sm text-white ring-1 ring-white/20 hover:bg-red-600 disabled:opacity-40"
                >
                  ×
                </button>
              ))}
          </figure>
        ))}
      </div>
      {error && <p className="mt-2 text-xs text-red-300">{error}</p>}
    </>
  );
}

function FullScreenMessage({ title, body, onExit }: { title: string; body: string; onExit: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black px-6 text-center">
      <h1 className="mb-2 text-2xl font-bold">{title}</h1>
      {body && <p className="mb-6 text-sm text-white/55">{body}</p>}
      <button onClick={onExit} className="rounded-md bg-white px-6 py-3 text-sm font-bold text-black">
        Go back
      </button>
    </div>
  );
}
